import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useField } from 'formik'
import { useTranslations } from 'next-intl'
import { useDebounce } from 'utils/useDebounce'
import { PositionOption } from '../PositionOption/PositionOption'
import { IFormValue } from '../../../validator/IFormValues'
import { CreatableSelectField } from '../../CreatableSelectField/CreatableSelectField'
import style from './FormInstituteField.module.css'
import { FormFieldTypes } from '../../../emun/RegistrationFormFieldTypes'
import { IDictionaryItem } from '../../../interfaces/IDictionaryItem'
import { INSTITUTE_FIELD_NAME, INSTITUTE_FIELD_PLACEHOLDER } from '../constants'
import { useInstituteFindByNameAndRegion } from '../../../api/query/useInstituteFindByNameAndRegion'

const DEFAULT_MIN_SEARCH_LENGTH = 0

const getInputValue = (inputValue: IDictionaryItem) =>
  inputValue.shortName ? `${inputValue.name} (${inputValue.shortName})` : inputValue.name

const renderItem = (option: IDictionaryItem, inputValue: IDictionaryItem, isActive: boolean) => (
  <PositionOption
    isActive={isActive}
    optionName={`${option.name} (${option.shortName})`}
    inputValue={getInputValue(inputValue)}
  />
)

const renderValue = (inputValue: IDictionaryItem) => getInputValue(inputValue)

type FormInstituteFieldFieldProps = {
  validate?: (name: string) => (currentValue: IFormValue) => Promise<string>
}

export const FormInstituteField: FC<FormInstituteFieldFieldProps> = ({ validate }) => {
  const t = useTranslations()
  const [search, setSearch] = useState('')
  const [currentRegionId, setCurrentRegionId] = useState('')
  const fieldName = t(INSTITUTE_FIELD_NAME)
  const fieldPlaceholder = t(INSTITUTE_FIELD_PLACEHOLDER)
  const [field, , helper] = useField(FormFieldTypes.Institute)
  const [regionField] = useField(FormFieldTypes.Region)
  const { data } = useInstituteFindByNameAndRegion(DEFAULT_MIN_SEARCH_LENGTH, {
    name: useDebounce(search),
    regionId: regionField.value.id,
  })

  useEffect(() => {
    if (field.value?.id) {
      setSearch(field.value.name)
    }
  }, [field.value?.id])

  useEffect(() => {
    const changedRegionId = regionField.value.id

    if (currentRegionId !== '' && changedRegionId !== currentRegionId) {
      helper.setValue({ id: '', name: '', shortName: '' })
    }

    setCurrentRegionId(changedRegionId)
  }, [regionField.value.id])

  const onChange = useCallback(
    (value: string): void => {
      if (value.length >= DEFAULT_MIN_SEARCH_LENGTH && !!regionField.value.id) {
        setSearch(value)
      }
    },
    [regionField],
  )

  const isDisabled = useMemo(() => !regionField.value.id, [regionField.value.id])

  return (
    <CreatableSelectField<IDictionaryItem>
      inputClassName={style.FormInstituteField}
      disabled={isDisabled}
      name={field.name}
      label={fieldName}
      options={data}
      onChange={onChange}
      placeholder={fieldPlaceholder}
      renderItem={renderItem}
      renderValue={renderValue}
      minSearchLength={DEFAULT_MIN_SEARCH_LENGTH}
      validate={validate && validate(field.name)}
    />
  )
}

FormInstituteField.displayName = 'FormInstituteField'
